// Account deletions: the admin page's rules (src/pages/Deletions.jsx), kept
// here so test/account-deletions.test.js can run them.
//
// A customer asks to delete their account from their dashboard. squish-backend
// holds the request for 14 days, and only while nothing on the account is live.
// Then the request waits on an admin, here. Approving runs the erasure, which
// cannot be undone, so the admin types the customer's email first; the server
// checks it against the same request (squish-crm-backend
// POST /api/admin/account-deletions/:uid/approve).

// Status → how a row reads, and in what order. The ones waiting on an admin
// come first.
export const DELETION_STATUS = {
  awaiting_approval: { label: 'Waiting on you', tone: 'red',   order: 0, approvable: true },
  blocked:           { label: 'On hold',        tone: 'amber', order: 1, approvable: false },
  scheduled:         { label: 'In its 14 days', tone: 'quiet', order: 2, approvable: false },
};

export function statusMeta(status) {
  return DELETION_STATUS[status] || { label: status ? String(status) : 'Unknown', tone: 'quiet', order: 9, approvable: false };
}

// Why a held request is held (squish-backend blockedReason).
export function holdReason(reason) {
  if (reason === 'still-holds') return 'Something on the account is live again. It carries on by itself once that is cancelled.';
  if (reason === 'needs-support') return 'The engine could not work this account out on its own. It needs a hand check.';
  if (reason === 'refused') return 'The erasure refused when it was last run. It needs a hand check.';
  return '';
}

const ms = (iso) => {
  const t = Date.parse(iso || '');
  return Number.isFinite(t) ? t : Infinity;
};

// Waiting on you first, then on hold, then the ones still in their 14 days;
// within each, the one due soonest first.
export function boardRows(requests) {
  return [...(requests || [])]
    .filter((r) => r && r.uid)
    .sort((a, b) => (statusMeta(a.status).order - statusMeta(b.status).order)
      || (ms(a.scheduledFor) - ms(b.scheduledFor)));
}

export const waitingCount = (requests) => (requests || []).filter((r) => statusMeta(r?.status).approvable).length;

// The typed confirmation. Case and surrounding spaces don't matter; anything
// else does. The server checks the same thing; this only keeps the button off.
export function emailMatches(typed, email) {
  const a = String(typed ?? '').trim().toLowerCase();
  const b = String(email ?? '').trim().toLowerCase();
  return a.length > 0 && a === b;
}

// What the admin is told after approving.
export function approveOutcome(result) {
  if (result?.complete) {
    return { ok: true, text: `Deleted.${result.auditRecordId ? ` Erasure record ${result.auditRecordId}.` : ''}` };
  }
  return { ok: false, text: 'It ran, but the erasure record says it did not finish. Check the record before anything else.' };
}

export function shortDate(iso) {
  const t = Date.parse(iso || '');
  if (!Number.isFinite(t)) return '—';
  return new Date(t).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}
